import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import set from 'lodash/set';
import { useState } from 'react';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { getFQDNFromString, isUrlValid, KTSVG } from '../../../../../../_metronic/helpers';
import { api } from '../../../../../modules/api/api';
import { useAuth } from '../../../../../modules/auth';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import InfoTooltip from '../../../../../modules/components/tooltip/InfoTooltip';
import { SiService, siteIntelligenceServiceLabel, Vpn } from '../../../mappings';
import { UsePageDataManagerResult } from '../hooks/usePageDataManager';
import { schema } from './Schema';

interface Props {
  data: any;
  onClose: () => void;
  onUpdate: UsePageDataManagerResult['updateField'];
  onSave: UsePageDataManagerResult['saveService'];
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Splits a string of URLs by comma, pipe, semicolon, or whitespace.
 */
const splitUrls = (inputString) => {
  // Remove duplicate and trailing commas
  const cleanedUrls = inputString.trim().replace(/,+/g, ',').replace(/,+$/, '');
  if (!cleanedUrls) return [];

  // Split by comma
  return inputString.split(/[,]/).filter(Boolean);
};

const AddInOutboundLinks = ({
  data,
  onClose,
  onUpdate,
  onSave,
  isLoading,
  setIsLoading,
}: Props) => {
  const intl = useIntl();
  const { auth, refresh } = useAuth();
  const { setToast } = useToast();

  const [errorMessage, setErrorMessage] = useState('');

  const service = data.service;

  const { addInboundOutboundSchema } = schema(intl);
  const defaultValues: z.infer<typeof addInboundOutboundSchema> = {
    links: [
      {
        foundLink: '',
        sourceLinks: '',
        title: '',
        description: '',
        vpns: [Vpn.ASIA],
      },
    ],
  };
  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    watch,
    register,
    getFieldState,
    resetField,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(addInboundOutboundSchema),
    defaultValues,
  });
  const { fields, append, prepend, remove, swap, move, insert } = useFieldArray({
    control,
    name: 'links',
  });

  const vpnOptions = [{ value: Vpn.ASIA, label: 'Asia' }];

  const handleFetchMetadata = async (index: number) => {
    const foundLink = getValues(`links.${index}.foundLink`);

    setIsLoading(true);

    try {
      const { result, status } = await api(
        'admin/v1/fetch_url_metadata',
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            url: foundLink,
          }),
        },
        { token: auth?.accessToken, refresh },
        'site-intelligence'
      ).then(async (resp) => {
        const result = await resp?.json();
        return { result: result, status: result?.code ?? resp?.status };
      });

      const resolvedResult = await result;
      if (status === 200 && resolvedResult?.code === 200 && resolvedResult?.error === false) {
        const results = resolvedResult.results;
        const title = results?.title;
        const description = results?.description;
        if (!title && !description) {
          throw new Error('Fail to fetch metadata.');
        }
        if (title) {
          setValue(`links.${index}.title`, title);
        }
        if (description) {
          setValue(`links.${index}.description`, description);
        }
      } else {
        throw new Error(resolvedResult?.message ?? 'Fail to fetch metadata.');
      }
    } catch (err) {
      setToast({
        show: true,
        type: 'Danger',
        header: err?.message || intl.formatMessage({ id: 'MESSAGE.ERROR' }),
        message: 'Fail to fetch metadata.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setErrorMessage('');
    onClose();
  };

  const onSubmit: SubmitHandler<typeof defaultValues> = async (formData) => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      const newData = structuredClone(data);
      const fqdnSet = new Set<string>(newData?.result?.fqdn_group?.fqdn ?? []);

      if (!newData?.detail || !Array.isArray(newData?.detail)) {
        set(newData, 'detail', []);
      }

      formData.links.forEach((link, index) => {
        fqdnSet.add(getFQDNFromString(link.foundLink));

        link.vpns.forEach((vpn, vIndex) => {
          const splitSourceLinks = splitUrls(link.sourceLinks.trim());
          splitSourceLinks.forEach((sourceLink, sIndex) => {
            newData.detail.push({
              found_link: link.foundLink.trim(),
              source_link: sourceLink,
              title: link.title?.trim() || null,
              description: link.description?.trim() || null,
              vpn: vpn,
            });
          });
        });
      });

      set(newData, 'result.fqdn_group', {
        count: fqdnSet.size,
        fqdn: Array.from(fqdnSet),
      });

      if (
        [SiService.OUTBOUND_FLOW_SERVICE, SiService.SOCIAL_OUTBOUND_FLOW_SERVICE].includes(service)
      ) {
        set(newData, 'result.total_found_links', newData.detail.length);
      }

      await onSave(service, null, newData);

      handleClose();
    } catch (err) {
      setToast({
        show: true,
        type: 'Danger',
        header: intl.formatMessage({ id: 'MESSAGE.ERROR' }),
        message: 'Fail to add category.',
      });
      setErrorMessage(err?.message || intl.formatMessage({ id: 'MESSAGE.AN_ERROR_OCCURRED' }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='d-flex flex-row-fluid flex-center bg-white rounded'>
      <form className='pt-5 w-100 w-xl-900px px-md-9' onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className='vstack gap-9'>
          {fields.map((field, index) => (
            <div
              key={field.id}
              className='position-relative card card-custom card-flush p-4 border border-gray-300 border-dashed gap-4'
            >
              {index > 0 && (
                <div
                  className='position-absolute top-0 start-100'
                  style={{ translate: '-50% -20%' }}
                >
                  <i
                    className={clsx('bi bi-dash-square-fill fs-1 text-danger cursor-pointer')}
                    title='Remove'
                    onClick={() => remove(index)}
                  ></i>
                </div>
              )}

              <div className='d-flex flex-column flex-md-row align-items-md-center'>
                <label className='form-label required w-md-50'>Location</label>
                <div className='w-100 d-flex gap-4 gap-md-8'>
                  {vpnOptions.map((option, index) => {
                    const id = `${option.label}-checkbox`;
                    return (
                      <div key={index} className='form-check'>
                        <Controller
                          name={`links.${index}.vpns`}
                          control={control}
                          render={({ field, fieldState }) => {
                            return (
                              <input
                                id={id}
                                type='checkbox'
                                value=''
                                defaultChecked={defaultValues.links[0].vpns.includes(option.value)}
                                disabled={isLoading}
                                className={clsx(
                                  'form-check-input',
                                  fieldState.invalid && 'border border-danger'
                                )}
                                onChange={(e) => {
                                  field.onChange(
                                    e.target.checked
                                      ? [option.value, ...field.value]
                                      : field.value.filter((item) => item !== option.value)
                                  );
                                }}
                              />
                            );
                          }}
                        />
                        <label className='form-check-label' htmlFor={id}>
                          {option.label}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Inbound / Outbound / Social Media Link */}
              <div className='d-flex flex-column flex-md-row align-items-md-center'>
                <label className='form-label required w-md-50'>
                  Found {siteIntelligenceServiceLabel[service]} Link{' '}
                  <small>(must include http:// or https://)</small>
                </label>
                <div className='w-100 d-flex'>
                  <Controller
                    name={`links.${index}.foundLink`}
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <input
                          {...field}
                          disabled={isLoading}
                          type='text'
                          className={clsx(
                            'form-control fw-normal fs-base',
                            fieldState.invalid && 'border border-danger'
                          )}
                          placeholder='https://other-domain.com'
                        />
                      );
                    }}
                  />
                  <div>
                    <button
                      className='btn btn-primary text-nowrap d-flex align-items-center gap-2'
                      type='button'
                      disabled={isLoading || !isUrlValid(watch(`links.${index}.foundLink`), true)}
                      onClick={() => handleFetchMetadata(index)}
                    >
                      Get Data
                    </button>
                  </div>
                </div>
              </div>

              {/* Current Site Links */}
              <div className='d-flex flex-column flex-md-row align-items-md-center'>
                <label className='form-label required w-md-50 d-flex align-items-center'>
                  Current Site Links
                  <InfoTooltip
                    id='current_site_links_tooltip'
                    placement='bottom'
                    iconFontSize='fs-2'
                    className='ps-2'
                  >
                    <p className='m-0'>
                      Separate URLs with a comma <code>,</code>. Include http:// or https://
                    </p>
                  </InfoTooltip>
                </label>
                <Controller
                  name={`links.${index}.sourceLinks`}
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <textarea
                        {...field}
                        disabled={isLoading}
                        className={clsx(
                          'form-control fw-normal fs-base',
                          fieldState.invalid && 'border border-danger'
                        )}
                        placeholder='https://example.com,https://example.com/products'
                      />
                    );
                  }}
                />
              </div>

              <div className='d-flex flex-column flex-md-row align-items-md-center'>
                <label className='form-label w-md-50'>Title</label>
                <Controller
                  name={`links.${index}.title`}
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <input
                        {...field}
                        disabled={isLoading}
                        type='text'
                        className={clsx(
                          'form-control fw-normal fs-base',
                          fieldState.invalid && 'border border-danger'
                        )}
                      />
                    );
                  }}
                />
              </div>

              <div className='d-flex flex-column flex-md-row align-items-md-center'>
                <label className='form-label w-md-50'>Description</label>
                <Controller
                  name={`links.${index}.description`}
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <textarea
                        {...field}
                        disabled={isLoading}
                        className={clsx(
                          'form-control fw-normal fs-base',
                          fieldState.invalid && 'border border-danger'
                        )}
                      />
                    );
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className='mt-4'>
          <i
            className='bi bi-plus-square-fill fs-1 text-success cursor-pointer'
            onClick={() => {
              append(defaultValues.links[0]);
            }}
          ></i>
        </div>

        {errorMessage && (
          <div className='text-danger my-4 border border-danger rounded p-2'>{errorMessage}</div>
        )}

        <div className='d-flex justify-content-end mt-4'>
          <button
            className='btn btn-primary d-flex align-items-center gap-2'
            type='submit'
            disabled={isLoading}
          >
            {intl.formatMessage({ id: 'COMMON.SUBMIT' })}
            {isLoading ? (
              <span className='spinner-border spinner-border-sm align-middle'></span>
            ) : (
              <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-3 m-0' />
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export { AddInOutboundLinks };
